import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { serviceItemService } from 'services/serviceItemService';
import { CreateServiceItemDto, UpdateServiceItemDto } from 'types/models';

const QUERY_KEY = 'service-items';
const CATALOG_QUERY_KEY = ['catalog-services', 'active'];

export function useServiceItems() {
  return useQuery({
    queryKey: [QUERY_KEY],
    queryFn: () => serviceItemService.listAll(),
  });
}

export function useServiceItem(id: string) {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () => serviceItemService.getById(id),
    enabled: !!id,
  });
}

export function useCreateServiceItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateServiceItemDto) => serviceItemService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: CATALOG_QUERY_KEY });
    },
  });
}

export function useUpdateServiceItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateServiceItemDto }) =>
      serviceItemService.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: CATALOG_QUERY_KEY });
    },
  });
}

export function useToggleServiceItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, activate }: { id: string; activate: boolean }) =>
      activate ? serviceItemService.activate(id) : serviceItemService.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: CATALOG_QUERY_KEY });
    },
  });
}
